/** Default UI locale from panel hostname (doc 13 § 14 — PROMPT-20). */

export const I18N_STORAGE_KEY = "I18N_LANGUAGE";

export const SUPPORTED_LOCALES = ["pt-BR", "en", "es"] as const;

export type AdatisLocale = (typeof SUPPORTED_LOCALES)[number];

const isSupportedLocale = (value: string): value is AdatisLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

/** Hosts of the global market SPA (`panel.adatis.app`, etc.). Marketing apex does not load this file. */
export const isGlobalAppHost = (hostname: string): boolean => {
  const host = (hostname || "").toLowerCase();

  return host === "adatis.app" || host.endsWith(".adatis.app");
};

export const isBrazilHost = (hostname: string): boolean => {
  const host = (hostname || "").toLowerCase();

  return host === "adatis.com.br" || host.endsWith(".adatis.com.br");
};

/** Returns a supported locale, or null if the value is missing/unknown. */
export const normalizeLocale = (
  value: string | null | undefined
): AdatisLocale | null => {
  if (!value) {
    return null;
  }

  if (value === "sp") {
    return "es";
  }

  if (isSupportedLocale(value)) {
    return value;
  }

  return null;
};

/**
 * Host default when the user has no saved `I18N_LANGUAGE`.
 * - `*.adatis.app` → `en`, or `es` if `navigator.language` starts with `es`
 * - `*.adatis.com.br` / localhost / other → `pt-BR`
 */
export const resolveHostDefaultLocale = (
  hostname: string,
  navigatorLanguage?: string | null
): AdatisLocale => {
  if (isGlobalAppHost(hostname)) {
    const nav = (navigatorLanguage || "").toLowerCase();

    if (nav.startsWith("es")) {
      return "es";
    }

    return "en";
  }

  return "pt-BR";
};

/**
 * Initial SPA locale: saved preference wins; otherwise host (+ optional navigator) default.
 */
export const resolveInitialLocale = (options: {
  hostname: string;
  storedLocale?: string | null;
  navigatorLanguage?: string | null;
}): AdatisLocale => {
  const stored = normalizeLocale(options.storedLocale);

  if (stored) {
    return stored;
  }

  return resolveHostDefaultLocale(
    options.hostname,
    options.navigatorLanguage
  );
};
